import "@/index.css";
import { API_CONFIGURED } from "@/lib/api";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/Login";
import CompanySetup from "@/pages/CompanySetup";
import ActivateAccount from "@/pages/ActivateAccount";
import QuotesPage from "@/pages/Quotes";
import QuoteEditorPage from "@/pages/QuoteEditor";
import MaterialsPage, { MaterialCostsPage } from "@/pages/Materials";
import RateTablesPage from "@/pages/RateTables";
import DocumentSettingsPage from "@/pages/DocumentSettings";
import AdminSettings, { LaserSettings, MaterialSettings, SecondarySettings } from "@/pages/AdminSettings";
import PolicySettings, { CAMSettings } from '@/pages/PolicySettings';
import Customers from '@/pages/Customers';
import UserSettings from '@/pages/UserSettings';
import LaserSettingsEditor from "@/pages/LaserSettingsEditor";
import MachinesPage from "@/pages/Machines";
import GeometryJobsPage from "@/pages/GeometryJobs";
import FilesPage from "@/pages/Files";
import Dashboard from "@/pages/Dashboard";
import Production from "@/pages/Production";

function Protected({ children }) {
  const { session, loading, error, refresh } = useAuth();
  if (loading) return <div className="p-8 text-slate-500 font-mono text-sm">Loading…</div>;
  if (error && !session) return <div role="alert" className="p-8">{error}<button className="ml-4 underline" onClick={refresh}>Retry connection</button></div>;
  if (!session) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { session } = useAuth();
  return session?.user?.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  if (!API_CONFIGURED) return <main className="min-h-screen bg-slate-50 flex items-center justify-center p-8"><div role="alert" className="max-w-lg rounded-xl border bg-white p-8"><h1 className="text-xl font-semibold">SmartQuote needs its server connection configured</h1><p className="mt-3 text-slate-600">This preview build is missing its API address. Ask your administrator to rebuild it with the correct server connection.</p></div></main>;
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/activate" element={<ActivateAccount />} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/setup" element={<AdminOnly><CompanySetup /></AdminOnly>} />
            <Route path="/quotes" element={<QuotesPage view="all" />} />
            <Route path="/quotes/new" element={<QuotesPage view="all" createOnMount />} />
            <Route path="/quotes/expired" element={<QuotesPage view="expired" />} />
            <Route path="/quotes/near-expiry" element={<QuotesPage view="expiring" />} />
            <Route path="/quotes/draft" element={<QuotesPage view="draft" />} />
            <Route path="/quotes/pending-approval" element={<QuotesPage view="approval" />} />
            <Route path="/quotes/:id" element={<QuoteEditorPage />} />
            <Route path="/customers" element={<Customers/>}/>
            <Route path="/customers/new" element={<Customers createOnMount />}/>
            <Route path="/orders" element={<Production view="orders" />} />
            <Route path="/orders/nesting" element={<Production view="nesting" />} />
            <Route path="/orders/schedule" element={<Production view="schedule" />} />
            <Route path="/orders/production" element={<Production view="board" />} />
            <Route path="/orders/material-requirements" element={<Production view="requirements" />} />
            <Route path="/materials" element={<MaterialsPage />} />
            <Route path="/material-costs" element={<MaterialCostsPage />} />
            <Route path="/settings" element={<AdminOnly><AdminSettings /></AdminOnly>}>
              <Route index element={<Navigate to="company-branding" replace />} />
              <Route path="company-branding" element={<DocumentSettingsPage />} />
              <Route path="users" element={<UserSettings />} />
              <Route path="cam-integration" element={<CAMSettings />} />
              {['general','payment-terms','bending','workflow','quote-defaults','approval'].map(section=><Route key={section} path={section} element={<PolicySettings key={section} section={section}/>}/>)}
              <Route path="secondary-processes" element={<SecondarySettings/>}>
                <Route index element={<PolicySettings section="secondary-processes"/>}/>
                <Route path="deburr" element={<PolicySettings section="deburr"/>}/>
              </Route>
              <Route path="materials" element={<MaterialSettings />}>
                <Route index element={<Navigate to="policy" replace/>}/>
                <Route path="policy" element={<PolicySettings section="material-recovery"/>}/>
              </Route>
              <Route path="laser" element={<LaserSettings />}>
                <Route index element={<Navigate to="rates" replace />} />
                <Route path="rates" element={<RateTablesPage />} />
                <Route path="machines" element={<MachinesPage />} />
                <Route path="process" element={<LaserSettingsEditor section="process"/>}/>
                <Route path="material" element={<LaserSettingsEditor section="material"/>}/>
                <Route path="defaults" element={<LaserSettingsEditor section="defaults"/>}/>
                <Route path="nesting" element={<LaserSettingsEditor section="nesting"/>}/>
                <Route path="advanced" element={<LaserSettingsEditor section="advanced"/>}/>
              </Route>
            </Route>
            <Route path="/document-settings" element={<AdminOnly><Navigate to="/settings/company-branding" replace /></AdminOnly>} />
            <Route path="/machines" element={<AdminOnly><Navigate to="/settings/laser/machines" replace /></AdminOnly>} />
            <Route path="/rate-tables" element={<AdminOnly><Navigate to="/settings/laser/rates" replace /></AdminOnly>} />
            <Route path="/geometry-jobs" element={<GeometryJobsPage />} />
            <Route path="/files" element={<FilesPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}


